import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import "./StaticFiles.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function StaticFiles() {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin";

  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([{ _id: null, name: "Root" }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals / Actions state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderCreating, setFolderCreating] = useState(false);

  const [uploading, setUploading] = useState(false);
  
  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("");

  // Preview Modal
  const [previewFile, setPreviewFile] = useState(null);

  // Fetch directory contents
  const fetchItems = async (folderId) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE}/static-items?parentId=${folderId || "null"}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (response.ok) {
        setItems(data.items || []);
        setBreadcrumbs(data.breadcrumbs || [{ _id: null, name: "Root" }]);
      } else {
        setError(data.message || "Failed to fetch files.");
      }
    } catch (err) {
      console.error(err);
      setError("Network error loading files.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentFolderId);
  }, [currentFolderId]);

  const handleNavigate = (folderId) => {
    setCurrentFolderId(folderId);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setFolderCreating(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/static-items/folder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newFolderName.trim(),
          parentId: currentFolderId,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setNewFolderName("");
        setShowFolderModal(false);
        fetchItems(currentFolderId);
      } else {
        setError(data.message || "Failed to create folder.");
      }
    } catch (err) {
      console.error(err);
      setError("Network error creating folder.");
    } finally {
      setFolderCreating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);
    if (currentFolderId) {
      formData.append("parentId", currentFolderId);
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/static-items/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        fetchItems(currentFolderId);
      } else {
        setError(data.message || "Failed to upload file.");
      }
    } catch (err) {
      console.error(err);
      setError("Network error uploading file.");
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = "";
    }
  };

  const handleDeleteItem = async (itemId, name, type) => {
    const confirmMsg = `Are you sure you want to delete ${type} "${name}"? ${
      type === "folder" ? "All files and folders inside it will be deleted permanently!" : ""
    }`;
    if (!window.confirm(confirmMsg)) return;

    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/static-items/${itemId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        fetchItems(currentFolderId);
      } else {
        setError(data.message || "Failed to delete item.");
      }
    } catch (err) {
      console.error(err);
      setError("Network error deleting item.");
    }
  };

  // Helper to choose file preview or download
  const handleFileAction = (item) => {
    const fileUrl = item.url.startsWith("http") ? item.url : `${API_BASE}${item.url}`;
    const ext = item.name.split(".").pop().toLowerCase();
    
    if (ext === "pdf" || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) {
      setPreviewFile({ ...item, fullUrl: fileUrl, ext });
    } else {
      // Force download for other files
      window.open(fileUrl, "_blank");
    }
  };

  const getFileIcon = (name) => {
    const ext = name.split(".").pop().toLowerCase();
    if (ext === "pdf") return "📕";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "🖼️";
    if (["doc", "docx"].includes(ext)) return "📘";
    if (["xls", "xlsx"].includes(ext)) return "📗";
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return "📦";
    if (["mp4", "mkv", "avi", "mov"].includes(ext)) return "🎥";
    if (["mp3", "wav", "aac"].includes(ext)) return "🎵";
    return "📄";
  };

  // Format bytes
  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Filter items based on search query
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="static-files-page">
      {/* Top Header */}
      <div className="files-header">
        <div className="header-info">
          <h1>Syllabus & Study Files</h1>
          <p>Browse courses, subjects, syllabi, and reference sheets in a clean directory structure.</p>
        </div>

        {/* Search */}
        <div className="files-search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Admin Actions Bar */}
      {isAdmin && (
        <div className="admin-actions-bar">
          <button className="btn-create-folder" onClick={() => setShowFolderModal(true)}>
            📁 Create Folder
          </button>
          
          <label className="btn-upload-file">
            📤 Upload File
            <input type="file" onChange={handleFileUpload} style={{ display: "none" }} />
          </label>

          {uploading && (
            <div className="uploading-indicator">
              <div className="mini-spinner"></div>
              <span>Uploading file...</span>
            </div>
          )}
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div className="files-error-alert">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="error-close-btn" onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <div className="files-breadcrumbs">
        {breadcrumbs.map((crumb, idx) => (
          <React.Fragment key={crumb._id || "root"}>
            <span
              className={`breadcrumb-link ${idx === breadcrumbs.length - 1 ? "active" : ""}`}
              onClick={() => handleNavigate(crumb._id)}
            >
              {crumb.name}
            </span>
            {idx < breadcrumbs.length - 1 && <span className="breadcrumb-separator">/</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Explorer Content */}
      {loading ? (
        <div className="files-loading">
          <div className="spinner"></div>
          <p>Loading files explorer...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-files-card">
          <div className="empty-icon">📁</div>
          <h3>This folder is empty</h3>
          <p>No files or folders have been added here yet.</p>
        </div>
      ) : (
        <div className="files-grid">
          {filteredItems.map((item) => (
            <div
              key={item._id}
              className={`file-card ${item.type === "folder" ? "folder-card" : "file-item-card"}`}
              onClick={() => (item.type === "folder" ? handleNavigate(item._id) : handleFileAction(item))}
            >
              {/* Delete button (Admin only) */}
              {isAdmin && (
                <button
                  className="delete-item-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem(item._id, item.name, item.type);
                  }}
                  title={`Delete ${item.type}`}
                >
                  &times;
                </button>
              )}

              <div className="card-icon-container">
                <span className="card-large-icon">
                  {item.type === "folder" ? "📁" : getFileIcon(item.name)}
                </span>
              </div>

              <div className="card-details">
                <h4 className="card-name" title={item.name}>
                  {item.name}
                </h4>
                <p className="card-meta">
                  {item.type === "folder" ? (
                    "Directory"
                  ) : (
                    <span>
                      {formatBytes(item.size)} • {item.name.split(".").pop().toUpperCase()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Folder Creation Modal */}
      {showFolderModal && (
        <div className="files-modal-overlay" onClick={() => setShowFolderModal(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Folder</h3>
              <button className="modal-close" onClick={() => setShowFolderModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateFolder}>
              <div className="modal-body">
                <label htmlFor="folderName">Folder Name</label>
                <input
                  type="text"
                  id="folderName"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="e.g. Artificial Intelligence, Syllabus"
                  required
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowFolderModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-confirm" disabled={folderCreating}>
                  {folderCreating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF/Image Preview Modal */}
      {previewFile && (
        <div className="files-modal-overlay preview-overlay" onClick={() => setPreviewFile(null)}>
          <div className="files-modal preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-box">
                <span className="modal-icon">
                  {previewFile.ext === "pdf" ? "📕" : "🖼️"}
                </span>
                <h3>{previewFile.name}</h3>
              </div>
              <div className="modal-header-actions">
                <a
                  href={previewFile.fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-modal-link"
                >
                  🔗 Open External
                </a>
                <button className="modal-close" onClick={() => setPreviewFile(null)}>
                  &times;
                </button>
              </div>
            </div>
            <div className="modal-body preview-body">
              {previewFile.ext === "pdf" ? (
                <iframe
                  src={previewFile.fullUrl}
                  title={previewFile.name}
                  className="preview-iframe"
                />
              ) : (
                <div className="image-preview-container">
                  <img src={previewFile.fullUrl} alt={previewFile.name} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
